import { WBFlex, WBTextFieldV2 } from '@admiin-com/ds-web';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useNewTaskNumber } from '../../../../hooks/useNewTaskNumber';
import { Bill } from '../../PayBillCreateForm';
import { SignPayFormTo } from './components/SignPayFormTo';
import { SignPayLineItems } from './components/SignPayLineItems';
import { SignPayRoundedOptions } from './components/SignPayRoundedOptions';
import { getNestedError } from './utils';
import { SignPayPRN } from './components/SignPayPRN';
import DueDateSelectorV2 from '../../../DueDateSelector/DueDateSelectorV2';
import { TASK_FORM_FIELDS } from '../../../../helpers/form';
import { useTaskCreationContext } from '../../../../features/providers/TaskCreationProvider/useTaskCreationContext';
import { TaskSettlementStatus } from '@admiin-com/ds-graphql';

export const BillPayForm = ({
  isNonAbnEntity,
  scannedTax,
}: {
  isNonAbnEntity?: boolean;
  scannedTax: Bill | null;
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { taskDirection, taskType } = useTaskCreationContext();

  const isTaxType = React.useMemo(() => taskType === 'tax', [taskType]);

  const { placeholder: newInvoiceId } = useNewTaskNumber();
  const inputs = React.useMemo(
    () => ({
      invoiceId: {
        label: t('invoiceId', { ns: 'taskbox' }),
        name: `futureTaskInput.reference`,
        type: 'text',
        placeholder: t('invoiceIdPlaceholder', { ns: 'taskbox' }),
        defaultValue: taskDirection === 'SENDING' ? newInvoiceId : '',
        rules: isTaxType
          ? {}
          : { required: t('referenceRequired', { ns: 'taskbox' }) },
      },
      amount: {
        name: 'futureTaskInput.amount',
        defaultValue: 0,
        type: 'number',
        placeholder: t('amount', { ns: 'taskbox' }),
        label: t('amount', { ns: 'taskbox' }),
        rules: {
          required: t('amountRequired', { ns: 'taskbox' }),
          validate: (value: number) =>
            value > 0 || t('amountMustBeGreaterThanZero', { ns: 'taskbox' }),
        },
      },
      dueAt: {
        ...TASK_FORM_FIELDS.dueAt,
        name: `futureTaskInput.dueAt`,
      },
      issuedAt: {
        ...TASK_FORM_FIELDS.issuedAt,
        name: `futureTaskInput.issuedAt`,
      },
    }),
    [t, taskDirection, newInvoiceId, isTaxType]
  );

  const settlementStatus = useWatch({ name: 'settlementStatus' });

  const isRefundable =
    (settlementStatus === TaskSettlementStatus.REFUNDABLE ||
      settlementStatus === TaskSettlementStatus.NIL) &&
    isTaxType;

  const isNil = settlementStatus === TaskSettlementStatus.NIL && isTaxType;

  return (
    <>
      {!isRefundable && (
        <Controller
          control={control}
          name={inputs.invoiceId.name}
          rules={inputs.invoiceId.rules}
          defaultValue={inputs.invoiceId.defaultValue}
          render={({ field, formState: { errors } }) =>
            isTaxType ? (
              <div />
            ) : (
              <WBTextFieldV2
                {...field}
                type={inputs.invoiceId.type}
                placeholder={inputs.invoiceId.placeholder}
                label={inputs.invoiceId.label}
                key={field.name}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                fullWidth
              />
            )
          }
        />
      )}
      <SignPayFormTo isNonAbnEntity={isNonAbnEntity} scannedTax={scannedTax} />

      {isTaxType && !isNil ? (
        <Controller
          name={inputs.amount.name}
          control={control}
          rules={inputs.amount.rules}
          defaultValue={inputs.amount.defaultValue}
          render={({ field, formState: { errors } }) => {
            return (
              <WBTextFieldV2
                {...field}
                type={inputs.amount.type}
                placeholder={inputs.amount.placeholder}
                label={inputs.amount.label}
                key={field.name}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
              />
            );
          }}
        />
      ) : null}
      <WBFlex flexDirection={['column', 'row']} gap={isTaxType ? 0 : [0, 4]}>
        <Controller
          control={control}
          name={inputs.issuedAt.name}
          rules={inputs.issuedAt.rules}
          defaultValue={inputs.issuedAt.defaultValue}
          render={({ field, formState: { errors } }) =>
            isTaxType ? (
              <div />
            ) : (
              <DueDateSelectorV2
                {...field}
                placeholder={inputs.issuedAt.placeholder}
                label={inputs.issuedAt.label}
                fullWidth
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                error={!!getNestedError(errors, field.name)}
              />
            )
          }
        />
        <Controller
          control={control}
          name={inputs.dueAt.name}
          rules={inputs.dueAt.rules}
          defaultValue={inputs.dueAt.defaultValue}
          render={({ field, formState: { errors } }) =>
            isRefundable ? (
              <div />
            ) : (
              <DueDateSelectorV2
                {...field}
                placeholder={inputs.dueAt.placeholder}
                label={inputs.dueAt.label}
                fullWidth
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                error={!!getNestedError(errors, field.name)}
              />
            )
          }
        />
      </WBFlex>
      {!isRefundable && <SignPayPRN scannedTax={scannedTax} />}
      <SignPayRoundedOptions />

      {!isRefundable && <SignPayLineItems scannedTax={scannedTax} />}
    </>
  );
};
